import { Injectable } from '@angular/core';
import { GiftCard } from '../../giftcard/models/giftcard';
import { GiftCardService } from '../../giftcard/services/giftcard.service';
import { LoggerService } from '../../../services/logger/logger.service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PartnerStateService } from '../../partner/services/partner-state.service';

@Injectable()
export class PaymentGiftStateService {
  private _redeemedGiftCards = new Array<GiftCard>();

  constructor(
    private giftCardService: GiftCardService,
    private logger: LoggerService,
    private partnerStateService: PartnerStateService,
  ) {
  }

  addGiftCardForRedeem(
    orderNumber: number,
    giftCardNumber: string,
    requestedAmount: number,
  ): Observable<boolean> {
    return of(false).pipe(
      switchMap(_ => {
        const card = this._redeemedGiftCards.find(x => x.cardNumber === giftCardNumber);
        if (card || requestedAmount <= 0) {
          return of(false);
        }

        return this._getGiftCardAvailableAmount(orderNumber, giftCardNumber, requestedAmount).pipe(
          map(amount => {
            this.logger.info(`addGiftCardForRedeem getting giftCardNumber: '${giftCardNumber}' requestedAmount: ${requestedAmount} amount: ${amount}`);
            if (amount) {
              this._redeemedGiftCards.push(new GiftCard(giftCardNumber, amount, ''));
              return true;
            } else {
              this.logger.info(`PaymentStateService: getGiftCardAvailableAmount failed. amount <= 0`);
              return false;
            }
          }),
          catchError(error => {
            this.logger.info(`PaymentStateService: getGiftCardAvailableAmount failed. error: ${error}`);
            return of(false);
          }),
        );
      }),
    );
  }

  private _getGiftCardAvailableAmount(orderNumber: number, giftCardNumber: string, orderAmount: number): Observable<number> {
    if (this.partnerStateService.loggedInPartner == null) {
      return this.giftCardService.getGuestGiftCardAvailableAmount(orderNumber, giftCardNumber, orderAmount);
    }

    return this.giftCardService.getGiftCardAvailableAmount(orderNumber, giftCardNumber, orderAmount);
  }

  recalculateGiftCardForRedeem(orderNumber, requestedAmount: number): Observable<boolean> {
    return this._recalculateGiftCardAmount(orderNumber, this._redeemedGiftCards.map(x => x.cardNumber), requestedAmount).pipe(
      map(
        result => {
          if (result) {
            this._redeemedGiftCards = result;
            return true;
          }
          return false;
        }
      ));
  }

  private _recalculateGiftCardAmount(orderNumber: number, giftCardNumbers: string[], orderAmount: number): Observable<GiftCard[]> {
    if (this.partnerStateService.loggedInPartner == null) {
      return this.giftCardService.guestRecalculateGiftCardAmount(orderNumber, giftCardNumbers, orderAmount);
    }

    return this.giftCardService.recalculateGiftCardAmount(orderNumber, giftCardNumbers, orderAmount);
  }

  get redeemedGiftCards(): GiftCard[] {
    return this._redeemedGiftCards;
  }

  get totalAmountRedeemedGiftCards(): number {
    return GiftCard.getTotalCardsAmount(this._redeemedGiftCards);
  }

  deleteRedeemedGiftCard(redeemedGiftCard: GiftCard): void {
    const index = this._redeemedGiftCards.indexOf(redeemedGiftCard);
    if (index > -1) {
      this._redeemedGiftCards.splice(index, 1);
    }
  }

  reset(): void {
    this._redeemedGiftCards = new Array<GiftCard>();
  }

  loadRedeemedGiftCards(redeemedGiftCards: GiftCard[]): void {
    if (redeemedGiftCards == null || redeemedGiftCards.length === 0) {
      this._redeemedGiftCards = new Array<GiftCard>();
      return;
    }

    this._redeemedGiftCards = redeemedGiftCards;
  }

}
